import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Gift, Plus, Edit2, Trash2, X, Loader2, Save, DollarSign, 
  Copy, Search, CreditCard, TrendingUp, Image as ImageIcon, Palette
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGiftCards = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('templates');
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Issued cards state
  const [issuedCards, setIssuedCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({ totalIssued: 0, totalValue: 0, activeCards: 0, activeBalance: 0 });
  
  // Search
  const [searchCards, setSearchCards] = useState('');

  // Template form
  const [templateForm, setTemplateForm] = useState({
    name: '', description: '', imageUrl: '', backgroundColor: '#DCC3B0', textColor: '#1a1a1a',
    denominations: '25,50,100,150,200', allowCustomAmount: true, minAmount: '10', maxAmount: '1000',
    expirationMonths: '12', termsAndConditions: '', active: true
  });

  // Issue form
  const [issueForm, setIssueForm] = useState({
    templateId: '', amount: '', recipientName: '', recipientEmail: '',
    senderName: '', senderEmail: '', personalMessage: '', deliveryDate: ''
  });

  useEffect(() => {
    fetchTemplates();
    fetchIssuedCards();
    fetchStats();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await fetch(`${API}/gift-cards/templates?practiceId=demo-practice-001`);
      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchIssuedCards = async () => {
    try {
      setLoadingCards(true);
      const response = await fetch(`${API}/gift-cards/issued?practiceId=demo-practice-001`);
      const data = await response.json();
      setIssuedCards(data);
    } catch (err) {
      console.error('Error fetching cards:', err);
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/gift-cards/stats?practiceId=demo-practice-001`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    setSavingTemplate(true);
    try {
      const data = {
        ...templateForm,
        denominations: templateForm.denominations.split(',').map(d => parseFloat(d.trim())).filter(Boolean),
        minAmount: parseFloat(templateForm.minAmount) || 10,
        maxAmount: parseFloat(templateForm.maxAmount) || 1000,
        expirationMonths: parseInt(templateForm.expirationMonths) || 12,
        practiceId: 'demo-practice-001'
      };

      const url = editingTemplate ? `${API}/gift-cards/templates/${editingTemplate.id}` : `${API}/gift-cards/templates`;
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save template');
      toast({ title: editingTemplate ? 'Template Updated' : 'Template Created' });
      resetTemplateForm();
      fetchTemplates();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save template.', variant: 'destructive' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    setSavingCard(true);
    try {
      const data = {
        ...issueForm,
        amount: parseFloat(issueForm.amount),
        practiceId: 'demo-practice-001'
      };

      const response = await fetch(`${API}/gift-cards/issue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to issue gift card');
      const result = await response.json();
      toast({ title: 'Gift Card Issued!', description: `Code: ${result.code}` });
      resetIssueForm();
      fetchIssuedCards();
      fetchStats();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to issue gift card.', variant: 'destructive' });
    } finally {
      setSavingCard(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name || '', description: template.description || '',
      imageUrl: template.imageUrl || '', backgroundColor: template.backgroundColor || '#DCC3B0',
      textColor: template.textColor || '#1a1a1a',
      denominations: (template.denominations || []).join(','),
      allowCustomAmount: template.allowCustomAmount !== false,
      minAmount: template.minAmount?.toString() || '10', maxAmount: template.maxAmount?.toString() || '1000',
      expirationMonths: template.expirationMonths?.toString() || '12',
      termsAndConditions: template.termsAndConditions || '', active: template.active !== false
    });
    setShowTemplateForm(true);
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    try {
      await fetch(`${API}/gift-cards/templates/${template.id}`, { method: 'DELETE' });
      toast({ title: 'Template deleted' });
      fetchTemplates();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete template.', variant: 'destructive' });
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({ title: 'Copied!', description: `Code ${code} copied to clipboard.` });
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '', description: '', imageUrl: '', backgroundColor: '#DCC3B0', textColor: '#1a1a1a',
      denominations: '25,50,100,150,200', allowCustomAmount: true, minAmount: '10', maxAmount: '1000',
      expirationMonths: '12', termsAndConditions: '', active: true
    });
    setEditingTemplate(null);
    setShowTemplateForm(false);
  };

  const resetIssueForm = () => {
    setIssueForm({
      templateId: '', amount: '', recipientName: '', recipientEmail: '',
      senderName: '', senderEmail: '', personalMessage: '', deliveryDate: ''
    });
    setShowIssueForm(false);
  };

  const filteredCards = issuedCards.filter(c =>
    c.code?.toLowerCase().includes(searchCards.toLowerCase()) ||
    c.recipientName?.toLowerCase().includes(searchCards.toLowerCase()) ||
    c.recipientEmail?.toLowerCase().includes(searchCards.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      redeemed: 'bg-blue-100 text-blue-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || styles.active}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-gift-cards">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift className="h-6 w-6 text-purple-500" />
            Gift Cards
          </h1>
          <p className="text-gray-600">Create and manage gift card templates and issued cards</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Total Issued</p>
            <p className="text-2xl font-bold">{stats.totalIssued}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Total Value</p>
            <p className="text-2xl font-bold">${stats.totalValue?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Active Cards</p>
            <p className="text-2xl font-bold text-green-600">{stats.activeCards}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Active Balance</p>
            <p className="text-2xl font-bold text-green-600">${stats.activeBalance?.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="issued">Issued Cards</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => setShowTemplateForm(true)}>
              <Plus className="h-4 w-4 mr-2" />Create Template
            </Button>
          </div>

          {loadingTemplates ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : templates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="text-center py-12">
                <Gift className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No gift card templates</h3>
                <p className="text-gray-500 mb-4">Create your first template</p>
                <Button onClick={() => setShowTemplateForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />Create Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="overflow-hidden">
                  <div className="h-24 flex items-center justify-center" style={{ backgroundColor: template.backgroundColor }}>
                    {template.imageUrl ? (
                      <img src={template.imageUrl} alt={template.name} className="h-full object-cover" />
                    ) : (
                      <Gift className="h-12 w-12" style={{ color: template.textColor }} />
                    )}
                  </div>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{template.name}</h3>
                      {!template.active && <Badge variant="secondary">Inactive</Badge>}
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{template.description || 'No description'}</p>
                    <div className="text-sm text-gray-600 mb-3">
                      <p>Amounts: ${template.denominations?.join(', $')}</p>
                      <p>Expires: {template.expirationMonths} months</p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Issued Cards Tab */}
        <TabsContent value="issued" className="space-y-6">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search by code, name, or email..." value={searchCards} onChange={(e) => setSearchCards(e.target.value)} className="pl-10" />
            </div>
            <Button onClick={() => setShowIssueForm(true)}>
              <Plus className="h-4 w-4 mr-2" />Issue Gift Card
            </Button>
          </div>

          {loadingCards ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredCards.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="text-center py-12">
                <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No gift cards issued</h3>
                <p className="text-gray-500 mb-4">Issue your first gift card</p>
                <Button onClick={() => setShowIssueForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />Issue Gift Card
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-3 text-xs text-gray-500 uppercase">Code</th>
                      <th className="py-3 text-xs text-gray-500 uppercase">Recipient</th>
                      <th className="py-3 text-xs text-gray-500 uppercase">Amount</th>
                      <th className="py-3 text-xs text-gray-500 uppercase">Balance</th>
                      <th className="py-3 text-xs text-gray-500 uppercase">Status</th>
                      <th className="py-3 text-xs text-gray-500 uppercase">Expires</th>
                      <th className="py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCards.map((card) => (
                      <tr key={card.id} className="border-b hover:bg-gray-50">
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm">{card.code}</code>
                            <Button variant="ghost" size="sm" onClick={() => copyCode(card.code)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="py-4">
                          <div>
                            <p className="font-medium">{card.recipientName || '-'}</p>
                            <p className="text-sm text-gray-500">{card.recipientEmail || '-'}</p>
                          </div>
                        </td>
                        <td className="py-4">${card.amount}</td>
                        <td className="py-4 font-semibold text-green-600">${card.balance}</td>
                        <td className="py-4">{getStatusBadge(card.status)}</td>
                        <td className="py-4 text-gray-600">{card.expirationDate || 'Never'}</td>
                        <td className="py-4">
                          <Button variant="ghost" size="sm">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Template Form Modal */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetTemplateForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTemplateSubmit} className="space-y-4">
                <div>
                  <Label>Template Name *</Label>
                  <Input value={templateForm.name} onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} placeholder="e.g., Classic Gift Card" required className="mt-1" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={templateForm.description} onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})} placeholder="A perfect gift for any occasion" rows={2} className="mt-1" />
                </div>
                <div>
                  <Label>Image URL</Label>
                  <Input value={templateForm.imageUrl} onChange={(e) => setTemplateForm({...templateForm, imageUrl: e.target.value})} placeholder="https://example.com/image.jpg" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Background Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={templateForm.backgroundColor} onChange={(e) => setTemplateForm({...templateForm, backgroundColor: e.target.value})} className="w-12 h-10" />
                      <Input value={templateForm.backgroundColor} onChange={(e) => setTemplateForm({...templateForm, backgroundColor: e.target.value})} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Text Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={templateForm.textColor} onChange={(e) => setTemplateForm({...templateForm, textColor: e.target.value})} className="w-12 h-10" />
                      <Input value={templateForm.textColor} onChange={(e) => setTemplateForm({...templateForm, textColor: e.target.value})} className="flex-1" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Denominations (comma-separated)</Label>
                  <Input value={templateForm.denominations} onChange={(e) => setTemplateForm({...templateForm, denominations: e.target.value})} placeholder="25,50,100,150,200" className="mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Min Amount</Label>
                    <Input type="number" value={templateForm.minAmount} onChange={(e) => setTemplateForm({...templateForm, minAmount: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Max Amount</Label>
                    <Input type="number" value={templateForm.maxAmount} onChange={(e) => setTemplateForm({...templateForm, maxAmount: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Expires (months)</Label>
                    <Input type="number" value={templateForm.expirationMonths} onChange={(e) => setTemplateForm({...templateForm, expirationMonths: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Terms & Conditions</Label>
                  <Textarea value={templateForm.termsAndConditions} onChange={(e) => setTemplateForm({...templateForm, termsAndConditions: e.target.value})} rows={2} className="mt-1" />
                </div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={templateForm.allowCustomAmount} onChange={(e) => setTemplateForm({...templateForm, allowCustomAmount: e.target.checked})} />
                  <span className="text-sm">Allow custom amounts</span>
                </label>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetTemplateForm}>Cancel</Button>
                  <Button type="submit" disabled={savingTemplate}>
                    {savingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Issue Gift Card Modal */}
      {showIssueForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Issue Gift Card</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetIssueForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleIssueSubmit} className="space-y-4">
                <div>
                  <Label>Template *</Label>
                  <select value={issueForm.templateId} onChange={(e) => setIssueForm({...issueForm, templateId: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" required>
                    <option value="">Select a template</option>
                    {templates.filter(t => t.active).map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Amount *</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input type="number" value={issueForm.amount} onChange={(e) => setIssueForm({...issueForm, amount: e.target.value})} placeholder="100" required className="pl-10" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Recipient Name</Label>
                    <Input value={issueForm.recipientName} onChange={(e) => setIssueForm({...issueForm, recipientName: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Recipient Email</Label>
                    <Input type="email" value={issueForm.recipientEmail} onChange={(e) => setIssueForm({...issueForm, recipientEmail: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Sender Name</Label>
                    <Input value={issueForm.senderName} onChange={(e) => setIssueForm({...issueForm, senderName: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Sender Email</Label>
                    <Input type="email" value={issueForm.senderEmail} onChange={(e) => setIssueForm({...issueForm, senderEmail: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Personal Message</Label>
                  <Textarea value={issueForm.personalMessage} onChange={(e) => setIssueForm({...issueForm, personalMessage: e.target.value})} rows={2} className="mt-1" />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetIssueForm}>Cancel</Button>
                  <Button type="submit" disabled={savingCard}>
                    {savingCard ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift className="h-4 w-4 mr-2" />}
                    Issue Card
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminGiftCards;
